import { useState, useEffect } from 'react';
import { useHUser } from './hooks/HUsers';
import { useRequests } from './hooks/HRequests';
import { usePermissions } from './hooks/HPermissions';
import GRequestsTable from '../tables/GRequestsTable';
import { Snackbar, Alert, TextField, Chip } from '@mui/material';

/**
 * RequestsTab displays user settings, allows adding/editing users, and manages roles.
 * Integrates with the new HUsers hook for user invitation and filtering.
 *
 * @returns {JSX.Element} The rendered RequestsTab component.
 */

export default function RequestsTab({ user }: { user?: any } = {}) {
    // Debug on mount
    useEffect(() => {
        try {
            console.debug('[RequestsTab] mounted');
        } catch (e) {}
    }, []);

    const {
        // User data
        users,
        currentUser,
    } = useHUser();

    console.debug('[RequestsTab] hook currentUser=', currentUser, 'prop user=', user);

    const { loading: requestsLoading, error: requestsError, loadAllRequests, loadRequestsByOwner, loadRequestsByUser } = useRequests();
    const { createPermission, removePermission } = usePermissions();
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });




    /**
     * Handle search input change
     */

    const [ownerRequests, setOwnerRequests] = useState<any[]>([]);
    const [userRequests, setUserRequests] = useState<any[]>([]);
    const [approvingRequest, setApprovingRequest] = useState(false);

    const handleApproveRequest = async (userId: string | number, objectId: string | number, row?: any) => {
        console.debug('[RequestsTab] handleApproveRequest ENTER', { userId, objectId });

        const selectedUser = users.find((user) => String(user.id) === String(userId));
        let roleId = selectedUser?.role_id ?? selectedUser?.roleId;
        // Prefer role provided in the clicked row
        if ((roleId === undefined || roleId === null || String(roleId).trim() === '') && row) {
            roleId = row?.role_id ?? row?.roleId ?? row?.role ?? undefined;
            console.debug('[RequestsTab] resolved roleId from row', { roleId });
        }

        // Default role_id to 3 if still missing
        if (roleId === undefined || roleId === null || String(roleId).trim() === '') {
            console.debug('[RequestsTab] roleId missing - defaulting to 3');
            roleId = 3;
        }

        if (!userId) {
            console.debug('[RequestsTab] handleApproveRequest EARLY RETURN - missing userId');
            setSnackbar({ open: true, message: 'User information is missing.', severity: 'error' });
            return;
        }

        if (!objectId) {
            console.debug('[RequestsTab] handleApproveRequest EARLY RETURN - missing objectId');
            setSnackbar({ open: true, message: 'Object information is missing.', severity: 'error' });
            return;
        }

        setApprovingRequest(true);

        try {
            console.debug('[RequestsTab] calling createPermission', { userId, objectId, roleId });
            // Ensure we pass explicit user_id, object_id and role_id
            const created = await createPermission(userId, objectId, roleId);
            console.debug('[RequestsTab] createPermission returned', { created });
            if (!created) {
                console.debug('[RequestsTab] createPermission indicated failure');
                setSnackbar({ open: true, message: 'Failed to approve request.', severity: 'error' });
                return;
            }
            console.debug('[RequestsTab] permission created successfully, reloading requests');
            setSnackbar({
                open: true,
                message: 'Request approved and permission assigned successfully.',
                severity: 'success',
            });
            await loadAllRequests();
            console.debug('[RequestsTab] loadAllRequests complete');
        } catch (error) {
            console.error('[RequestsTab] Failed to approve request:', error);
            setSnackbar({ open: true, message: 'Failed to approve request.', severity: 'error' });
        } finally {
            setApprovingRequest(false);
            console.debug('[RequestsTab] handleApproveRequest EXIT', { userId, objectId });
        }
    };

    // expose for debug reproduction from browser console
    try {
        // @ts-ignore
        // attach to window so you can call from console: window.__handleApprove(userId, objectId)
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.__handleApprove = handleApproveRequest;
        console.debug('[RequestsTab] exposed window.__handleApprove for debugging');
    } catch (e) {}

    const handleRevokeRequest = async (userId: string | number, objectId: string | number) => {
        if (!userId) {
            setSnackbar({ open: true, message: 'User information is missing.', severity: 'error' });
            return;
        }

        if (!objectId) {
            setSnackbar({ open: true, message: 'Object information is missing.', severity: 'error' });
            return;
        }

        setApprovingRequest(true);

        try {

            await loadAllRequests();
        } catch (error) {
            console.error('Failed to revoke permission:', error);
            setSnackbar({ open: true, message: 'Failed to revoke permission.', severity: 'error' });
        } finally {
            setApprovingRequest(false);
        }
    };

    // Removed Tabs wrapper: always load requests when `userId` is available

    // Also load when component mounts or when currentUser becomes available,
    // to handle cases where the inner tab key isn't set to 'Requests'.
    useEffect(() => {
        // Mirror ObjectsTab/DashboardTab: depend on the `user` prop so effect runs
        // when TabRouter provides dbUser after mount.
        const uid =
            user?.id ?? user?.user_id ?? user?.user ?? currentUser?.id ?? currentUser?.user_id ?? currentUser?.user ?? null;

        console.debug('[RequestsTab] effect fired; prop user=', user, 'computed uid=', uid);
        if (uid) {
            (async () => {
                console.debug('[RequestsTab] calling loadAllRequests for uid=', uid);
                const all = await loadAllRequests();
                console.debug('[RequestsTab] loadAllRequests returned count=', Array.isArray(all) ? all.length : 0);

                const ownerList = Array.isArray(all)
                    ? all.filter((r: any) => String(r.object_owner_id) === String(uid))
                    : loadRequestsByOwner(uid);

                const userList = Array.isArray(all)
                    ? all.filter((r: any) => String(r.user) === String(uid))
                    : loadRequestsByUser(uid);

                setOwnerRequests(ownerList);
                setUserRequests(userList);
                try {
                    console.debug('[RequestsTab] (mount/currentUser effect) userRequests.count=', userList.length, 'ownerRequests.count=', ownerList.length);
                } catch (e) {}
            })();
        }
    // Note: do NOT include `loadRequestsByOwner` or `loadRequestsByUser` here because
    // they change identity whenever `requests` state updates in the hook, which
    // would retrigger this effect and cause an infinite fetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, currentUser, loadAllRequests]);

    // compute counts
    const totalRequests = (ownerRequests?.length ?? 0) + (userRequests?.length ?? 0);

    return (
        <div className='requests-page'>
            <div className='standard-div'>
                <div className='standard-div-flex'>
                    <div className='deals-title-box'>
                        <h1 style={{ margin: 0 }}>Requests</h1>
                        {!requestsLoading && (
                            <Chip label={`${totalRequests} request${totalRequests !== 1 ? 's' : ''}`} size='small' color='primary' variant='outlined' />
                        )}
                    </div>
                </div>
                <div className='header-flex'>
                    <TextField size='small' placeholder='Search requests...' value={''} onChange={() => {}} className='search-field' />
                </div>
            </div>

            <div className='standard-div'>
                <h2>Requests Sent</h2>
            </div>
            <GRequestsTable
                requests={userRequests}
                loading={requestsLoading}
                error={requestsError}
                onApprove={handleApproveRequest}
                onRevoke={handleRevokeRequest}
                approving={null}
                user={user ?? currentUser}
            />

            <div style={{ padding: '12px 0' }}>
                <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '0 0 12px' }} />
            </div>

            <div className='standard-div'>
                <h2>Requests Received</h2>
            </div>
            <GRequestsTable
                requests={ownerRequests}
                loading={requestsLoading}
                error={requestsError}
                onApprove={handleApproveRequest}
                onRevoke={handleRevokeRequest}
                approving={approvingRequest}
                user={user ?? currentUser}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}
